import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Eye, UserCheck, UserCog, UserX } from 'lucide-react';
import { UserWithDetails, deleteUser, setUserActiveStatus } from '@/lib/api/users';
import { UserAvatar } from './UserAvatar';
import { useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import { OrganisationAccessSummary } from './OrganisationAccessSummary';

interface UserTableProps {
  users: UserWithDetails[];
  onUserClick: (userId: string) => void;
  onRefresh: () => void | Promise<unknown>;
}

// Role labels reflect actual permission scope (verified against route guards
// in MainApp.tsx): `admin` has access to all 22 admin pages, `company_admin`
// has access to a strict subset. So `admin` = Tenant Admin (broad), and
// `company_admin` = Branch Admin (scoped to one company/branch).
const roleConfig = {
  admin: { color: 'bg-purple-100 text-purple-700', icon: '👑', label: 'Tenant Admin' },
  healthcare_professional: { color: 'bg-green-100 text-green-700', icon: '👨‍⚕️', label: 'Healthcare Pro' },
  sales_rep: { color: 'bg-blue-100 text-blue-700', icon: '💼', label: 'Sales Rep' },
  company_admin: { color: 'bg-orange-100 text-orange-700', icon: '🏢', label: 'Branch Admin' },
  user: { color: 'bg-gray-100 text-gray-700', icon: '👤', label: 'User' },
};

export function UserTable({ users, onUserClick, onRefresh }: UserTableProps) {
  const navigate = useNavigate();
  const { isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  // On SSO/white-label tenants (e.g. Boston) the username is an internal,
  // auto-derived value users never see or use to log in — so we hide the
  // Username column there. Master MEDD keeps it (usernames are real logins).
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleteNameInput, setDeleteNameInput] = useState('');

  // EXACT match — only leading/trailing spaces are ignored. Typing a different
  // internal spacing (e.g. "Anthony  Stipanov" double-spaced) must NOT enable
  // delete. Stored names are clean single-spaced values.
  const cleanName = (n?: string) => (n ?? '').trim();

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    // Guard against accidental deletion: the admin must type the exact name.
    if (cleanName(deleteNameInput) !== cleanName(deleteConfirm.name)) return;

    const result = await deleteUser(deleteConfirm.id, { enterpriseHierarchy: hasEnterpriseHierarchy });
    if (result.success) {
      await onRefresh();
    }
    setDeleteConfirm(null);
    setDeleteNameInput('');
  };

  const handleToggleActive = async (user: UserWithDetails) => {
    const result = await setUserActiveStatus(user.id, !(user.is_active ?? true));
    if (result.success) onRefresh();
  };

  return (
    <div className="max-w-full overflow-hidden rounded-xl border border-border bg-background">
      <div className="max-w-full overflow-hidden">
        <table className="w-full table-fixed">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-[36%] px-3 py-4 text-left text-sm font-semibold text-foreground xl:w-[30%]">User</th>
              {isMasterTenant && (
                <th className="hidden px-3 py-4 text-left text-sm font-semibold text-foreground min-[1800px]:table-cell">Username</th>
              )}
              <th className="w-[34%] px-3 py-4 text-left text-sm font-semibold text-foreground xl:w-[28%]">
                {hasEnterpriseHierarchy ? 'Platform access' : 'Role'}
              </th>
              <th className="hidden px-3 py-4 text-left text-sm font-semibold text-foreground xl:table-cell xl:w-[17%]">
                {hasEnterpriseHierarchy ? 'Position' : 'Company'}
              </th>
              <th className="hidden px-3 py-4 text-left text-sm font-semibold text-foreground min-[1800px]:table-cell">Credits</th>
              <th className="hidden px-3 py-4 text-left text-sm font-semibold text-foreground min-[1800px]:table-cell">Sessions</th>
              <th className="hidden px-3 py-4 text-left text-sm font-semibold text-foreground min-[1800px]:table-cell">Joined</th>
              <th className="w-[9rem] px-2 py-4 text-right text-sm font-semibold text-foreground xl:w-[10rem]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.length === 0 ? (
              <tr>
                <td colSpan={isMasterTenant ? 8 : 7} className="px-6 py-12 text-center text-muted-foreground">
                  No users found. Try adjusting your filters.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.id}
                  className="group cursor-pointer transition-colors hover:bg-muted/50"
                  onClick={() => onUserClick(user.id)}
                >
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        avatarUrl={user.avatar_url}
                        displayName={user.display_name}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-semibold text-foreground">{user.display_name}</span>
                          {user.is_active === false && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 border border-slate-300">
                              <UserX className="h-3 w-3" /> Inactive
                            </span>
                          )}
                          {hasEnterpriseHierarchy && user.role_pending ? (
                            <span
                              className="inline-flex items-center rounded-full border border-dashed border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                              title="This account has been prepared but the person has not signed in yet"
                            >
                              Pending sign-in
                            </span>
                          ) : null}
                        </div>
                        <div className="truncate text-sm text-muted-foreground">{user.email}</div>
                        <div className="mt-1 text-xs text-muted-foreground xl:hidden">
                          {[user.job_title, user.company_name].filter(Boolean).join(' · ') || 'No position recorded'}
                        </div>
                      </div>
                    </div>
                  </td>
                  {isMasterTenant && (
                    <td className="hidden px-3 py-4 min-[1800px]:table-cell">
                      <div className="text-sm text-foreground font-mono">@{user.username}</div>
                    </td>
                  )}
                  <td className="px-3 py-4">
                    {hasEnterpriseHierarchy ? (
                      <OrganisationAccessSummary
                        assignments={user.organisation_assignments}
                        compact
                      />
                    ) : user.role_pending ? (
                      // Provisioned but never logged in: role is intent, not yet
                      // assigned — takes effect on the user's first sign-in.
                      <span
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-dashed border-amber-300"
                        title="Role takes effect when this user signs in for the first time"
                      >
                        <span>{roleConfig[user.role]?.icon || roleConfig.user.icon}</span>
                        <span>Pending: {roleConfig[user.role]?.label || 'User'}</span>
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          roleConfig[user.role]?.color || roleConfig.user.color
                        }`}
                      >
                        <span>{roleConfig[user.role]?.icon || roleConfig.user.icon}</span>
                        <span>{roleConfig[user.role]?.label || 'User'}</span>
                      </span>
                    )}
                  </td>
                  <td className="hidden px-3 py-4 xl:table-cell">
                    {user.company_name || user.job_title ? (
                      <div>
                        {user.job_title ? (
                          <div className="text-sm font-medium text-foreground">{user.job_title}</div>
                        ) : null}
                        {user.company_name ? (
                          <div className="text-xs text-muted-foreground">{user.company_name}</div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="hidden px-3 py-4 min-[1800px]:table-cell">
                    <div className="text-sm font-semibold text-foreground">{user.credits}</div>
                  </td>
                  <td className="hidden px-3 py-4 min-[1800px]:table-cell">
                    <div className="text-sm">
                      <div className="font-semibold text-foreground">{user.total_sessions || 0}</div>
                      {user.last_session_date && (
                        <div className="text-xs text-muted-foreground">
                          {new Date(user.last_session_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="hidden px-3 py-4 text-sm text-muted-foreground min-[1800px]:table-cell">{user.created_at}</td>
                  <td className="bg-background px-2 py-4 text-right transition-colors group-hover:bg-muted/50">
                    <div className="flex items-center justify-end gap-0.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUserClick(user.id);
                        }}
                        className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                        title="View details"
                      >
                        <Eye size={16} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/users/${user.id}/edit`);
                        }}
                        className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                        title="Edit user"
                      >
                        <Edit2 size={16} className="text-muted-foreground" />
                      </button>
                      {hasEnterpriseHierarchy && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const returnTo = encodeURIComponent(`/admin/users?person=${user.id}`);
                            navigate(`/admin/organisation?tab=people&person=${user.id}&action=role&returnTo=${returnTo}`);
                          }}
                          className="rounded-lg p-1.5 transition-colors hover:bg-primary/10"
                          title="Change role or unit"
                        >
                          <UserCog size={16} className="text-primary" />
                        </button>
                      )}
                      {/* Activate / deactivate — only where the deployment tracks
                          is_active (dedicated SSO projects like BSC). */}
                      {user.is_active !== undefined && user.is_active !== null && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(user);
                          }}
                          className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          {user.is_active ? (
                            <UserX size={16} className="text-amber-600" />
                          ) : (
                            <UserCheck size={16} className="text-green-600" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ id: user.id, name: user.display_name });
                        }}
                        className="rounded-lg p-1.5 transition-colors hover:bg-destructive/10"
                        title={hasEnterpriseHierarchy ? 'Remove user from organisation' : 'Delete user'}
                      >
                        <Trash2 size={16} className="text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-2xl">
                {hasEnterpriseHierarchy ? 'Remove User?' : 'Delete User?'}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              {hasEnterpriseHierarchy ? (
                <>
                  Remove <span className="font-semibold text-foreground">{cleanName(deleteConfirm?.name)}</span>{' '}
                  from the organisation access you manage? Their other organisation access and account history will be kept.
                </>
              ) : (
                <>
                  Are you sure you want to delete{' '}
                  <span className="font-semibold text-foreground">{cleanName(deleteConfirm?.name)}</span>? This
                  action cannot be undone and will permanently remove all associated data. Consider{' '}
                  <span className="font-medium">deactivating</span> the user instead — that locks them out but keeps their history.
                </>
              )}
            </AlertDialogDescription>
            {/* Boston request: require typing the user's name so a mis-click
                can never delete an account. */}
            <div className="mt-3 space-y-1.5">
              <label htmlFor="delete-confirm-name" className="text-sm text-muted-foreground">
                Type <span className="font-semibold text-foreground">{cleanName(deleteConfirm?.name)}</span> to confirm:
              </label>
              <input
                id="delete-confirm-name"
                type="text"
                autoComplete="off"
                value={deleteNameInput}
                onChange={(e) => setDeleteNameInput(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-destructive"
                placeholder={cleanName(deleteConfirm?.name)}
              />
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirm(null); setDeleteNameInput(''); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={cleanName(deleteNameInput) !== cleanName(deleteConfirm?.name)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40 disabled:pointer-events-none"
            >
              {hasEnterpriseHierarchy ? 'Remove User' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
